import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Link2, Users, Euro, TrendingUp, Clock, CheckCircle, 
  Copy, ExternalLink, Gift, Award, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AffiliateDashboard() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [code]);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/affiliate/${code}/dashboard`);
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Code affilié invalide');
      } else {
        setError('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier !');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">En attente</Badge>;
      case 'validated':
        return <Badge className="bg-blue-100 text-blue-700">Validé</Badge>;
      case 'paid':
        return <Badge className="bg-emerald-100 text-emerald-700">Payé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Card className="max-w-md mx-4 bg-white/10 border-white/20">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <Link2 className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Code invalide</h2>
            <p className="text-white/60 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                Retour à l'accueil
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { affiliate, stats, commissions, referral_link } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <span className="text-black font-bold text-lg">ŒM</span>
            </div>
            <span className="text-white font-semibold hidden sm:block">L'Œil du Marchand</span>
          </Link>
          <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 px-3 py-1">
            <Link2 className="w-4 h-4 mr-1" />
            Espace Affilié
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Bonjour {affiliate.name || 'Affilié'} ! 
            <span className="ml-2">👋</span>
          </h1>
          <p className="text-white/60">
            Voici votre tableau de bord affilié. Partagez votre lien et gagnez des commissions !
          </p>
        </div>

        {/* Referral Link Card */}
        <Card className="mb-8 bg-gradient-to-r from-amber-500/20 to-amber-600/10 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-amber-400 mb-1 flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Votre lien de parrainage
                </h3>
                <p className="text-white/60 text-sm">Partagez ce lien et gagnez {Math.round(affiliate.commission_rate * 100)}% sur chaque vente</p>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-black/30 text-amber-300 px-4 py-2 rounded-lg font-mono text-sm">
                  {referral_link}
                </code>
                <Button 
                  size="sm" 
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  onClick={() => copyToClipboard(referral_link)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div className="flex items-center gap-2 text-white/70">
                <span className="text-sm">Code :</span>
                <Badge className="bg-white/10 text-white font-mono">{affiliate.code}</Badge>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(affiliate.code)}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-white/60 text-sm">Filleuls</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.total_referrals}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(stats.referrals_by_type || {}).map(([type, count]) => (
                  <Badge key={type} variant="outline" className="text-xs text-white/60">
                    {type}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-white/60 text-sm">En attente</span>
              </div>
              <p className="text-3xl font-bold text-amber-400">{formatPrice(stats.pending_amount)}</p>
              <p className="text-white/40 text-xs mt-1">À valider par l'admin</p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-white/60 text-sm">À recevoir</span>
              </div>
              <p className="text-3xl font-bold text-blue-400">{formatPrice(stats.validated_amount)}</p>
              <p className="text-white/40 text-xs mt-1">Validé, paiement en cours</p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-white/60 text-sm">Total gagné</span>
              </div>
              <p className="text-3xl font-bold text-emerald-400">{formatPrice(stats.total_earnings)}</p>
              <p className="text-white/40 text-xs mt-1">Dont {formatPrice(stats.paid_amount)} versé</p>
            </CardContent>
          </Card>
        </div>

        {/* Commission Rate Info */}
        <Card className="mb-8 bg-white/5 border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Votre taux de commission</h3>
                  <p className="text-white/60 text-sm">
                    {affiliate.is_company ? 'Statut: Société (TVA applicable)' : 'Statut: Particulier (pas de TVA)'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-amber-400">{Math.round(affiliate.commission_rate * 100)}%</p>
                <p className="text-white/40 text-sm">sur chaque vente HT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Commissions History */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              Historique des commissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {commissions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-white/20" />
                </div>
                <p className="text-white/40 mb-2">Aucune commission pour le moment</p>
                <p className="text-white/60 text-sm">Partagez votre lien pour commencer à gagner !</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3 text-white/60 font-medium">Date</th>
                      <th className="text-left p-3 text-white/60 font-medium">Description</th>
                      <th className="text-right p-3 text-white/60 font-medium">Montant HT</th>
                      <th className="text-right p-3 text-white/60 font-medium">TVA</th>
                      <th className="text-right p-3 text-white/60 font-medium">Total TTC</th>
                      <th className="text-center p-3 text-white/60 font-medium">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((commission) => (
                      <tr key={commission.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 text-white/80 text-sm">{formatDate(commission.date)}</td>
                        <td className="p-3 text-white/80 text-sm max-w-xs truncate">{commission.description}</td>
                        <td className="p-3 text-right text-white/80">{formatPrice(commission.amount_ht)}</td>
                        <td className="p-3 text-right text-white/60">{formatPrice(commission.tva_amount)}</td>
                        <td className="p-3 text-right text-white font-semibold">{formatPrice(commission.amount_ttc)}</td>
                        <td className="p-3 text-center">{getStatusBadge(commission.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="mt-8 bg-white/5 border-white/10">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4">Comment ça marche ?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 font-bold">1</span>
                </div>
                <div>
                  <p className="text-white font-medium">Partagez votre lien</p>
                  <p className="text-white/60 text-sm">Envoyez votre lien de parrainage à vos contacts</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 font-bold">2</span>
                </div>
                <div>
                  <p className="text-white font-medium">Ils s'inscrivent</p>
                  <p className="text-white/60 text-sm">Vos filleuls créent un compte et souscrivent</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 font-bold">3</span>
                </div>
                <div>
                  <p className="text-white font-medium">Vous gagnez</p>
                  <p className="text-white/60 text-sm">{Math.round(affiliate.commission_rate * 100)}% de commission sur chaque paiement</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-white/40 text-sm">
          <p>© 2026 L'Œil du Marchand - Programme Affilié</p>
        </div>
      </footer>
    </div>
  );
}
